import { type CSSProperties, useEffect, useRef, useState } from "react";
import Button from "@assessment/components/atoms/button";
import Tooltip from "@assessment/components/atoms/tooltip";
import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import { Question } from "@assessment/types/assessmentOngoing";
import GetHeroIcon from "@assessment/utils/getHeroIcons";
import ShowExplanation from "../../hint-explanation/explanationView";
import ShowHint from "../../hint-explanation/showHint";
import RichTextEditor from "@lxp-app/components/molecules/rich-text-editor/richTextEditor";
import Modal from "@assessment/components/atoms/modal";
import ContentDeleteModal from "@lxp-app/components/molecules/modal-children/contentDeleteModal";
import {
  useAttemptQuestionMutation,
  useFlagQuestionMutation,
  useGetSaveSubjectiveQLQuery,
  useSaveCodeMutation,
  useSaveSubjectiveMutation,
  useStartQuestionMutation,
  useLazyHeartBeatApiQuery,
  useGetUploadConfigQuery
} from "@assessment/store/slices/api/assessmentApiSlice";
import { string } from "yup";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useShowErrors } from "../../../toasterWrapper";
import { Textarea } from "@headlessui/react";
import LoaderIcon from "@assessment/components/atoms/loaderIcon";
import { useParams } from "react-router-dom";
import AudioResponse from "./audioResponse";
import SubjectiveAnswerSubmission from "./subjectiveAnswerSubmission";
import { extractPlainText, saveAnswers } from "../../../saveAnswers";
import { useTranslation } from "react-i18next";

interface AssessmentSubjectiveQuestions {
  question: Question;
  sectionId: string;
  totalQuestionsInSection: number;
  changeActiveQuestion: (val: "next" | "previous") => void;
}

const AssessmentSubjectiveQuestion = ({
  question,
  sectionId,
  totalQuestionsInSection,
  changeActiveQuestion
}: AssessmentSubjectiveQuestions) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const showErrors = useShowErrors();
  const { scheduleId } = useParams();

  const hints = question?.hint || [];

  const [isHintLoading, setIsHintLoading] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showHintSection, setShowHintSection] = useState(false);
  const [revealedHintsCount, setRevealedHintsCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [attemptingQuestion, setAttemptingQuestion] = useState(false);
  const [text, setText] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullWidthEditor, setFullWidthEditor] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);
  const [questionWidth, setQuestionWidth] = useState(50); // Initial % width of question panel
  const [editorTheme, setEditorTheme] = useState<"vs-dark" | "light">(
    "vs-dark"
  );
  const [changeQn, setChangeQn] = useState<"next" | "previous">("next");

  const textRef = useRef(text);
  const questionRef = useRef(question);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [availableViewportHeight, setAvailableViewportHeight] = useState<
    number | null
  >(null);

  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [saveCodeAPi] = useSaveCodeMutation();
  const [flagQuestion] = useFlagQuestionMutation();
  const [startQuestion] = useStartQuestionMutation();
  const [attemptQuestion] = useAttemptQuestionMutation();
  const [heartBeatApi] = useLazyHeartBeatApiQuery();
  const [qnStatus, setQnStatus] = useState<string>("");

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const constants = useAppSelector((store) => store.AssessmentSlice.duration);

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  const responses = useAppSelector(
    (store) => store.AssessmentSlice.candidateResponse
  );
  const responsesFib = useAppSelector(
    (store) => store.AssessmentSlice.candidateFibResponse
  );
  const assessmentData = useAppSelector(
    (store) => store.AssessmentSlice.assessmentData
  );
  const unsavedResponse: any = responses[question.questionId];

  const assessmentProgress = useAppSelector(
    (state) => state.AssessmentSlice.assessmentProgress
  );

  const questionFlagged =
    assessmentProgress[sectionId].questions[question.questionId].flagged;

  const preAssessmentData = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData
  );

  const showQuestionTitle = useAppSelector(
    (state) =>
      state.AssessmentSlice.preAssessmentData.questionConfig.showQuestionTitle
  );

  const isLastQuestionInTheSection =
    question.orderInSection === totalQuestionsInSection;

  const { data: savedAnswers, isLoading } = useGetSaveSubjectiveQLQuery(
    {
      ongoingId: ongoingId,
      questionId: question?.questionId,
      questionVersion: question?.versionnumber
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const handleExplanationClick = () => {
    setShowExplanation((prev) => !prev);
  };
  // useEffect(() => {
  //   questionRef.current = question;
  //   // setHasSavedOnce(false);
  //   setRevealedHintsCount(0);
  //   if (
  //     question?.codingexplanation &&
  //     question?.codingexplanation?.length > 0 &&
  //     text?.length > 0
  //   ) {
  //     setShowExplanation(true);
  //   } else {
  //     setShowExplanation(false);
  //   }
  //   setShowHintSection(false);
  // }, [question?.questionId]);

  useEffect(() => {
    if (showExplanation) {
      const el = document.getElementById("explanation-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showExplanation]);

  useEffect(() => {
    setShowExplanation(false);
    if (assessmentProgress?.[sectionId]) {
      const qnList = assessmentProgress?.[sectionId]?.questions;
      if (question?.questionId in qnList) {
        if (qnList?.[question?.questionId]?.status)
          if (qnList?.[question?.questionId]?.status == "completed")
            setShowExplanation(true);
      }
    }
  }, [assessmentProgress, question?.questionId]);

  // useEffect(() => {
  //   setShowExplanation(false);
  //   if (savedAnswers?.getSaveSubjectiveQL?.data?.savedSubjective?.length > 0) {
  //     const savedTxt: string =
  //       savedAnswers?.getSaveSubjectiveQL?.data?.savedSubjective?.[0]
  //         ?.usersubmission;
  //     setText(savedTxt);
  //     if (
  //       question?.codingexplanation &&
  //       !!question?.codingexplanation?.length &&
  //       savedTxt?.length > 0
  //     ) {
  //       setShowExplanation(true);
  //     } else setShowExplanation(false);
  //   } else {
  //     setShowExplanation(false);
  //     setText("");
  //   }
  // }, [savedAnswers]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     heartBeatApiCall();
  //   }, constants?.autoSave * 1000);

  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    textRef.current = text;
  }, [text]);
  const autoSaveApi = async () => {
    const newTxt = textRef.current;
    if (newTxt?.length > 0) {
      await saveAndComplete();
    }
    //  else heartBeatApiCall();
  };
  // const heartBeatApiCall = async () => {
  //   try {
  //     if (!scheduleId) {
  //       throw {
  //         status: "",
  //         title: "Schedule Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     if (!ongoingId) {
  //       throw {
  //         status: "",
  //         title: "Ongoing Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     const heartBeatRes = await heartBeatApi({
  //       ongoingId: ongoingId,
  //       scheduleId: scheduleId
  //     });
  //     if (heartBeatRes?.data?.data?.ongoingQPDuration) {
  //       dispatch(
  //         AssessmentSliceActions.mutateTimeLeft(
  //           heartBeatRes?.data?.data?.ongoingQPDuration
  //         )
  //       );
  //     }
  //   } catch (error: any) {
  //     console.log("error", error);
  //   }
  // };

  const saveAndComplete = async (isNext = false) => {
    setIsSaving(true);
    const newTxt = textRef.current;
    const qnRef = questionRef.current;
    try {
      const trimmedText = newTxt?.trim() || "";
      const isEmpty = trimmedText.length === 0;

      const res: any = await saveSubjectiveApi({
        ongoingId: ongoingId ?? "",
        questionId: qnRef?.questionId,
        quesVersion: qnRef?.versionnumber ?? 1,
        body: {
          solution: btoa(unescape(encodeURIComponent(newTxt))),
          // uploadConfigId: uploadCofigData?.data?.Id,
          fileDetails: {
            fileName: "subjective",
            fileSize: 10,
            fileExtension: ".txt",
            filePath: "string"
          }
        }
        // { solution: btoa(unescape(encodeURIComponent(newTxt))) }
      });
      if (res?.data?.data?.ongoingQpDuration) {
        dispatch(
          AssessmentSliceActions.mutateTimeLeft(
            res?.data?.data?.ongoingQpDuration
          )
        );
      }
      const res2 = await startQuestion({
        actionType: "completed",
        ongoingId: ongoingId ?? "",
        questionId: question.questionId
      });
      if (res2.error) {
        setAttemptingQuestion(false);
        throw {
          status: "",
          title: "Failed to complete the question",
          err: res2.error,
          description: ""
        };
      }
      dispatch(
        AssessmentSliceActions.mutateQuestionProgress({
          questionId: question.questionId,
          sectionId: sectionId,
          status: isEmpty ? "unanswered" : "completed"
        })
      );
      if (res) {
        dispatch(
          AssessmentSliceActions.mutateCandidateResponse({
            questionId: activeQuestionMeta?.questionId,
            answerKey: []
          })
        );
      }
      setIsSaving(false);
      if (isNext) changeActiveQuestion("next");
      else return;
    } catch (error: any) {
      console.log("error", error);
      setIsSaving(false);
    }
  };

  // useEffect(() => {
  //   // setHasSavedOnce(false);
  //   setRevealedHintsCount(0);
  //   if (
  //     question?.codingexplanation &&
  //     !!question?.codingexplanation?.length &&
  //     text?.length > 0
  //   ) {
  //     setShowExplanation(true);
  //   } else {
  //     setShowExplanation(false);
  //   }
  //   setShowHintSection(false);
  //   setIsHintLoading(false);
  // }, [question?.questionId]);

  // useEffect(() => {
  //   if (savedAnswers?.getSaveSubjectiveQL?.data?.savedSubjective?.length > 0) {
  //     const savedTxt: string =
  //       savedAnswers?.getSaveSubjectiveQL?.data?.savedSubjective?.[0]
  //         ?.usersubmission;
  //     setText(savedTxt);
  //     if (
  //       question?.codingexplanation &&
  //       !!question?.codingexplanation?.length &&
  //       savedTxt?.length > 0
  //     ) {
  //       setShowExplanation(true);
  //     }
  //   } else setText("");
  // }, [savedAnswers]);

  const attemptQuestionHandler = async (
    isFinalSubmit: boolean = false,
    revealExplanationOnly: boolean = false
  ) => {
    setAttemptingQuestion(true);
    try {
      if (question?.codingexplanation && !!question?.codingexplanation?.length)
        setShowExplanation(true);
      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }
      // saveAndComplete();
      if (revealExplanationOnly) {
        if (
          question?.codingexplanation &&
          !!question?.codingexplanation?.length
        )
          setShowExplanation(true);
        // setHasSavedAnswer(true);
      } else if (isFinalSubmit) {
        if (isLastQuestionInTheSection) {
          dispatch(AssessmentSliceActions.mutateShowSummaryModal(true));
        } else {
          setRevealedHintsCount(0);
          setShowHintSection(false);
          changeActiveQuestion("next");
        }
      }

      setAttemptingQuestion(false);
    } catch (err: any) {
      console.error(err);
      const temp = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  const flagQuestionHandler = async () => {
    try {
      setAttemptingQuestion(true);

      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const res = await flagQuestion({
        flag: questionFlagged ? "false" : "true",
        ongoingId: ongoingId,
        questionId: question.questionId
      });
      if (res.error) {
        throw {
          status: "",
          title: "Error updating the status of the quesiton",
          err: res.error,
          description: ""
        };
      }

      dispatch(
        AssessmentSliceActions.mutateQuestionFlagged({
          questionId: question.questionId,
          sectionId: sectionId,
          value: !questionFlagged
        })
      );
      setAttemptingQuestion(false);
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  const resetQuestionResponseHandler = async () => {
    setText("");

    try {
      if (!activeQuestionMeta?.questionId) return;
      setShowExplanation(false);
      setAttemptingQuestion(true);

      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }
      let callSaveApi = false;
      // if (text?.trim() !== "") {
      //     callSaveApi = true;
      // }

      const res: any = await saveSubjectiveApi({
        ongoingId: ongoingId ?? "",
        questionId: question?.questionId,
        quesVersion: question?.versionnumber ?? 1,
        body: { solution: "" }
      });

      if (res?.data?.data?.ongoingQpDuration) {
        dispatch(
          AssessmentSliceActions.mutateTimeLeft(
            res?.data?.data?.ongoingQpDuration
          )
        );
      }

      if (res) {
        dispatch(
          AssessmentSliceActions.mutateQuestionProgress({
            questionId: question.questionId,
            sectionId: sectionId,
            status: "unanswered"
          })
        );
        dispatch(
          AssessmentSliceActions.mutateCandidateResponse({
            questionId: activeQuestionMeta?.questionId,
            answerKey: []
          })
        );
      }
      setAttemptingQuestion(false);
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  const handleHintClick = () => {
    if (isHintLoading || revealedHintsCount >= (question?.hint?.length || 0))
      return;
    setShowHintSection(true);
    setRevealedHintsCount((prev) => prev + 1);
    setIsHintLoading(true);
    setProgress(0);

    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 2; // 2% every 100ms = 5s
      setProgress(currentProgress);

      if (currentProgress >= 100) {
        clearInterval(interval);
        setIsHintLoading(false);
        setProgress(0);
      }
    }, 100);
  };

  // useEffect(() => {
  //     console.log('IS FULLSCREEN', isFullScreen)
  //     if (isFullScreen) {
  //         document.body.classList.add('overflow-hidden');
  //     } else {
  //         document.body.classList.remove('overflow-hidden');
  //     }
  // }, [isFullScreen]);

  useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, []);

  useEffect(() => {
    const updateAvailableViewportHeight = () => {
      if (!containerRef.current) return;

      const topOffset = containerRef.current.getBoundingClientRect().top;
      const bottomSpacing = window.innerWidth >= 1024 ? 24 : 16;
      const nextHeight = Math.max(
        window.innerHeight - topOffset - bottomSpacing,
        320
      );

      setAvailableViewportHeight(nextHeight);
    };

    updateAvailableViewportHeight();
    window.addEventListener("resize", updateAvailableViewportHeight);
    window.addEventListener("orientationchange", updateAvailableViewportHeight);

    return () => {
      window.removeEventListener("resize", updateAvailableViewportHeight);
      window.removeEventListener(
        "orientationchange",
        updateAvailableViewportHeight
      );
    };
  }, []);

  const startDrag = () => {
    isDragging.current = true;
  };

  const stopDrag = () => {
    isDragging.current = false;
  };

  const onDrag = (e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const offsetLeft = container.getBoundingClientRect().left;
    const mouseX = e.clientX - offsetLeft;

    const newQuestionWidth = (mouseX / containerWidth) * 100;

    // Clamp between 10% and 60%
    if (newQuestionWidth >= 18 && newQuestionWidth <= 60) {
      setQuestionWidth(newQuestionWidth);
    }
  };

  const EllipsisRow = () => (
    <div className="flex">
      {[0, 1].map((i) => (
        <GetHeroIcon
          key={i}
          name="EllipsisVerticalIcon"
          variant="outline"
          className={`flex-shrink-0 w-5 h-5  ${i === 1 ? "-ml-4" : ""}
              ${editorTheme == "light" ? "text-gray-700" : "text-gray-50"}`}
        />
      ))}
    </div>
  );

  const handleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleSaveModal = () => {
    setSaveModalOpen(!saveModalOpen);
  };

  const handleQuestionNavigation = (direction: "next" | "previous") => {
    const typedText = extractPlainText(unsavedResponse?.value || "").trim();
    if (!typedText) {
      changeActiveQuestion(direction);
      return;
    }
    setChangeQn(direction);
    if (typedText.split(/\s+/).filter(Boolean).length >= 40) {
      handleSaveModal();
    } else {
      handleModal();
    }
  };

  const saveApiCall = async () => {
    const targetSection = assessmentData?.ongoingSections?.find(
      (section) => section.sectionId === sectionId
    );
    if (!targetSection) {
      changeActiveQuestion(changeQn);
      return;
    }
    await saveAnswers(
      targetSection,
      activeQuestionMeta,
      responses,
      ongoingId,
      dispatch,
      saveSubjectiveApi,
      startQuestion,
      saveCodeAPi,
      attemptQuestion,
      responsesFib,
      false,
      handleModal,
      () => changeActiveQuestion(changeQn)
    );
  };
  return (
    <>
      <div
        ref={containerRef}
        style={
          {
            "--question-width": `${questionWidth}%`,
            ...(availableViewportHeight
              ? {
                  "--subjective-panel-height": `${availableViewportHeight}px`
                }
              : {})
          } as CSSProperties
        }
        className="flex w-full min-h-0 flex-col gap-3 overflow-x-hidden lg:h-[var(--subjective-panel-height)] lg:max-h-[var(--subjective-panel-height)] lg:flex-row lg:gap-2"
      >
        {/* LEFT */}
        <div
          data-tour="acq-question"
          className="min-w-0 w-full shrink-0 px-4 py-6 md:px-5 md:py-8 border border-secondary/10 thin-scrollbar rounded-2xl dark:border-0 dark:bg-[#1E1E1E] lg:w-[var(--question-width)] lg:min-w-[18%] lg:max-w-[60%] lg:flex-[0_0_var(--question-width)] lg:self-stretch lg:overflow-hidden lg:hover:overflow-y-auto"
        >
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex-1 space-y-10 lg:min-h-0 lg:overflow-y-auto">
              <div className="flex items-centre justify-between">
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <div>
                    <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 truncate">
                      Question {question.orderInSection} /{" "}
                      {totalQuestionsInSection}
                    </h3>

                    {showQuestionTitle && question?.questiontitle && (
                      <div className="text-xl font-bold py-2">
                        {question?.questiontitle}
                      </div>
                    )}
                  </div>

                  {questionFlagged && (
                    <GetHeroIcon
                      name="FlagIcon"
                      className="w-5 h-5 text-[#AA6207]"
                    />
                  )}
                </div>

                {preAssessmentData?.questionConfig?.showQuestionMarks && (
                  <h3 className="text-base sm:text-lg font-bold leading-5 text-secondary">
                    Marks : {question?.maxMarks}
                  </h3>
                )}
              </div>

              <div
                dangerouslySetInnerHTML={{ __html: question.problemStatement }}
                className="mb-10 text-lg font-medium"
                style={{
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none"
                }}
              />

              {showExplanation &&
                preAssessmentData?.questionConfig?.showExplanation &&
                question?.codingexplanation &&
                !!question?.codingexplanation?.length && (
                  <div className="mt-10" id="explanation-section">
                    <p className="font-semibold text-secondary/90 mb-2">
                      Explanation
                    </p>
                    <ShowExplanation
                      options={[]}
                      codingExplanation={question.codingexplanation ?? []}
                      selectedOption={[text]}
                      subType={"fib"}
                    />
                  </div>
                )}

              {showHintSection &&
                preAssessmentData?.questionConfig?.showHint &&
                question?.hint?.length > 0 && (
                  <div className="mt-10" id="hints-section">
                    <p className="font-semibold text-secondary/90 mb-2">
                      Hints
                    </p>

                    {hints.slice(0, revealedHintsCount).map((hint, index) => (
                      <div
                        key={index}
                        id={`hint-${index}`}
                        className="mt-3 animate-fadeInUp"
                        style={{
                          animationDelay: `${index * 100}ms`,
                          animationFillMode: "both"
                        }}
                      >
                        <ShowHint hints={[hint]} visibleCount={1} />
                      </div>
                    ))}
                  </div>
                )}
            </div>

            <div className="mt-6 border-t border-secondary/10 bg-white py-2 dark:bg-[#1E1E1E] lg:mt-auto lg:sticky lg:bottom-0 lg:border-t-0">
              <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4 lg:flex-nowrap">
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2 md:w-auto md:max-w-[55%] md:flex-1 lg:max-w-none lg:flex-initial">
                  <Button
                    className="w-full text-[#AA6207] sm:w-auto"
                    variant="outlined"
                    buttonColor="secondary"
                    onClick={flagQuestionHandler}
                    disable={attemptingQuestion}
                  >
                    {questionFlagged ? "Unflag" : "Flag"}
                  </Button>

                  {question?.hint?.length > 0 &&
                    (() => {
                      const allHintsShown =
                        revealedHintsCount >= question.hint.length;

                      const tooltip = isHintLoading
                        ? "You need to wait before you see another hint"
                        : allHintsShown
                          ? "No hints are pending"
                          : "";

                      const btn = (
                        <Button
                          onClick={handleHintClick}
                          disabled={isHintLoading || allHintsShown}
                          variant="outlined"
                          buttonColor="secondary"
                          className="relative w-full text-[#FBBF24] sm:w-auto"
                        >
                          {isHintLoading && (
                            <div
                              className="absolute top-0 left-0 h-full bg-secondary-bg"
                              style={{ width: `${progress}%` }}
                            />
                          )}
                          <span className="relative z-10">
                            {t("assessment.subjective.showHint")}
                          </span>
                        </Button>
                      );

                      return tooltip ? (
                        <Tooltip message={tooltip} position="bottom-16">
                          <div className="w-full sm:w-auto">{btn}</div>
                        </Tooltip>
                      ) : (
                        btn
                      );
                    })()}

                  {preAssessmentData?.questionConfig?.showExplanation &&
                    question?.codingexplanation &&
                    question?.codingexplanation.length > 0 && (
                      <Button
                        className="w-full sm:w-auto"
                        buttonColor="secondary"
                        variant="outlined"
                        onClick={handleExplanationClick}
                        //   className="text-secondary"
                      >
                        {showExplanation
                          ? "Hide Explanation"
                          : "Show Explanation"}
                      </Button>
                    )}
                </div>

                {/* RIGHT BUTTONS */}
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-between md:w-auto md:flex-1 md:justify-end lg:flex-initial">
                  {/* PREVIOUS */}
                  {question.orderInSection > 1 && (
                    <Button
                      className="w-full sm:w-auto"
                      buttonColor="secondary"
                      variant="outlined"
                      onClick={() => handleQuestionNavigation("previous")}
                      disable={attemptingQuestion}
                    >
                      Previous
                    </Button>
                  )}

                  {!isLastQuestionInTheSection && (
                    <Button
                      className="w-full sm:w-auto"
                      buttonColor="primary"
                      variant="contained"
                      onClick={() => handleQuestionNavigation("next")}
                    >
                      Next
                    </Button>
                  )}

                  {isLastQuestionInTheSection && (
                    <Button
                      className="w-full sm:w-auto"
                      buttonColor="primary"
                      variant="contained"
                      onClick={() =>
                        dispatch(
                          AssessmentSliceActions.mutateShowSummaryModal(true)
                        )
                      }
                      disable={attemptingQuestion}
                    >
                      View Section Summary
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="w-full min-w-0 min-h-[22rem] overflow-y-auto thin-scrollbar rounded-2xl border dark:border-none md:min-h-[28rem] lg:min-h-0 lg:flex-1 lg:w-[calc(100%-var(--question-width))]">
          <SubjectiveAnswerSubmission
            sectionId={sectionId}
            question={question}
          />
        </div>
      </div>

      {/* RIGHT */}
      <div className="w-[50%] overflow-y-auto thin-scrollbar rounded-2xl border dark:border-none">
        <SubjectiveAnswerSubmission sectionId={sectionId} question={question} />
      </div>

      <Modal
        open={modalOpen}
        setOpen={handleModal}
        dialogueClassName="sm:max-w-3xl"
      >
        <ContentDeleteModal
          isShow={modalOpen}
          show={() => {
            dispatch(
              AssessmentSliceActions.mutateCandidateResponse({
                questionId: question.questionId,
                answerKey: []
              })
            );
            changeActiveQuestion(changeQn);
            handleModal();
          }}
          setOpen={handleModal}
          buttonName="Switch Question"
          showRemarksDeleteModal={false}
          commentsAllowed={false}
          heading="Are you sure you want to leave?"
          msg={`You have unsaved changes. If you change the question, your response will be permanently deleted.`}
        />
      </Modal>
      <Modal
        open={saveModalOpen}
        setOpen={handleSaveModal}
        dialogueClassName="sm:max-w-3xl"
      >
        <ContentDeleteModal
          isShow={saveModalOpen}
          show={() => {
            saveApiCall();
            handleSaveModal();
          }}
          setOpen={handleSaveModal}
          buttonName="Save Changes?"
          showRemarksDeleteModal={false}
          commentsAllowed={false}
          heading="Do you want to save the changes?"
          onClose={() => {
            dispatch(
              AssessmentSliceActions.mutateCandidateResponse({
                questionId: question.questionId,
                answerKey: []
              })
            );
            changeActiveQuestion(changeQn);
          }}
          msg={`You have unsaved changes. Would you like to save them before changing the question?`}
        />
      </Modal>
    </>
  );
};

export default AssessmentSubjectiveQuestion;
